// Build the gemSpec_TI-M_Basis-mandated `X-TIM-User-Agent` header value.
//
// Format: `(TIM-Client) <client_id>, <client_version>, <OS_version>`
//
// The leading `(TIM-Client) ` prefix is required by the gematik SBG; without
// it the gateway returns 403 M_FORBIDDEN before the request reaches Synapse.

import packageJson from '../../package.json';

// A_17235 / Tab_ProdIdentZ: Hersteller-/Anbieter-ID (String[5], gematik-assigned)
// and Produktkürzel (String[8], manufacturer-defined). Update VENDOR_ID once
// gematik issues the official identifier for this product.
const VENDOR_ID = 'NSFRE';
const PRODUCT_KEY = 'TIMWEB01';

const CLIENT_ID = `${VENDOR_ID}.${PRODUCT_KEY}`;
const CLIENT_VERSION = packageJson.version;

const WIN_VERSION_MAP = {
  '10.0': 'Windows 10/11',
  '6.3': 'Windows 8.1',
  '6.2': 'Windows 8',
  '6.1': 'Windows 7',
};

// IMPORTANT: Order matters. Mobile devices often include desktop OS strings in
// their UA (iPad on iOS 13+ reports "Mac OS X"; Android UAs contain "Linux"),
// so mobile platforms MUST be checked before their desktop look-alikes.
function detectOSVersion(ua) {
  if (!ua) return 'Unknown';

  if (ua.includes('iPhone') || ua.includes('iPad')) {
    const match = ua.match(/OS ([\d_]+)/);
    return match ? `iOS ${match[1].replace(/_/g, '.')}` : 'iOS';
  }

  if (ua.includes('Android')) {
    const match = ua.match(/Android ([\d.]+)/);
    return match ? `Android ${match[1]}` : 'Android';
  }

  if (ua.includes('Mac OS X')) {
    const match = ua.match(/Mac OS X ([\d_]+)/);
    return match ? `macOS ${match[1].replace(/_/g, '.')}` : 'macOS';
  }

  if (ua.includes('Windows NT')) {
    const match = ua.match(/Windows NT ([\d.]+)/);
    if (match) {
      return WIN_VERSION_MAP[match[1]] || `Windows NT ${match[1]}`;
    }
    return 'Windows';
  }

  if (ua.includes('Linux')) {
    if (ua.includes('Ubuntu')) return 'Ubuntu';
    if (ua.includes('Fedora')) return 'Fedora';
    return 'Linux';
  }

  return 'Unknown';
}

export function buildTIMUserAgent(navigatorObj = typeof navigator !== 'undefined' ? navigator : null) {
  const ua = navigatorObj?.userAgent || '';
  return `(TIM-Client) ${CLIENT_ID}, ${CLIENT_VERSION}, ${detectOSVersion(ua)}`;
}

// Drop-in replacement for `fetch()` that adds the gemSpec_TI-M_Basis-mandated
// X-TIM-User-Agent header. Use at every call site that hits the TI-M
// homeserver or VZD — without this, the gematik SBG returns 403 M_FORBIDDEN
// before the request reaches Synapse. Pass-through for everything else
// (Authorization, body, signal, etc.) so it's a true drop-in.
//
// For matrix-js-sdk clients, prefer passing this as `fetchFn` to createClient.
//
// Header shape is preserved: if the caller passes a plain object, we return
// a plain object; if a Headers instance, we return a Headers instance; if an
// array of pairs, we return an array. This keeps existing test assertions
// (which often inspect `init.headers.Authorization` as a plain property)
// working unchanged.
export function timFetch(input, init = {}) {
  const safeInit = init || {};
  const orig = safeInit.headers;
  const ua = buildTIMUserAgent();
  let next;
  if (orig instanceof Headers) {
    next = new Headers(orig);
    if (!next.has('X-TIM-User-Agent')) next.set('X-TIM-User-Agent', ua);
  } else if (Array.isArray(orig)) {
    const has = orig.some(([k]) => typeof k === 'string' && k.toLowerCase() === 'x-tim-user-agent');
    next = has ? orig.slice() : [...orig, ['X-TIM-User-Agent', ua]];
  } else {
    next = { ...(orig || {}) };
    const has = Object.keys(next).some((k) => k.toLowerCase() === 'x-tim-user-agent');
    if (!has) next['X-TIM-User-Agent'] = ua;
  }
  return fetch(input, { ...safeInit, headers: next });
}
